import { useState, useRef, useEffect } from 'react';
import { MessageSquarePlus, Send, X } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';

interface InlineCommentInputProps {
    itemId: string;
    onCommentAdded: () => void;
}

export function InlineCommentInput({ itemId, onCommentAdded }: InlineCommentInputProps) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setText('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!text.trim() || submitting) return;

        setSubmitting(true);
        try {
            await inventoryApi.addComment(itemId, text.trim());
            setText('');
            setOpen(false);
            onCommentAdded();
        } catch (err) {
            console.error('Failed to add comment:', err);
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) {
        return (
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                className="rounded p-0.5 text-gray-400 hover:bg-blue-50 hover:text-blue-500"
                title="Quick comment"
            >
                <MessageSquarePlus className="h-3.5 w-3.5" />
            </button>
        );
    }

    return (
        <div ref={containerRef} className="absolute right-0 top-0 z-20" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSubmit} className="flex items-center gap-1 rounded-md border border-blue-300 bg-white px-2 py-1 shadow-md">
                <input
                    ref={inputRef}
                    type="text"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="Quick comment..."
                    maxLength={2000}
                    className="h-6 w-48 border-0 p-0 text-xs focus:outline-none focus:ring-0"
                />
                <button
                    type="submit"
                    disabled={!text.trim() || submitting}
                    className="rounded p-0.5 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                >
                    <Send className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    onClick={() => { setOpen(false); setText(''); }}
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-100"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </form>
        </div>
    );
}
